import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdOrdersOrderIdIdTickets,
  GetV1CompaniesCompanyIdOrdersOrderIdIdTicketsData,
  TicketSource,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { noop, omit } from 'lodash-es';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { FetchReason } from '~constants/enums';
import { Ticket } from '~hooks/useTickets/models';
import { extractPagination, Pagination } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { TICKETS_QUERY_CONTEXT_PREFIX } from './constants';
import { makeTicketsQueryContextKey } from './utils';

const derivePaginationQueryParams = (pageParam?: Pagination) => {
  return {
    'page[limit]': 20,
    'page[after]': pageParam?.after,
  };
};

const deriveFilterQueryParams = (filters: TicketsQueryFilters) => {
  const { flagged, fromDate, toDate, sources } = filters;

  const query: GetV1CompaniesCompanyIdOrdersOrderIdIdTicketsData['query'] = {
    'filter[flagged]': flagged,
    'filter[from_date]': fromDate
      ? dayjs.tz(fromDate).startOf('day').toISOString()
      : undefined,
    'filter[to_date]': toDate ? dayjs.tz(toDate).endOf('day').toISOString() : undefined,
    'filter[sources]': sources?.length ? sources : undefined,
  };

  return query;
};

type TicketsQueryFilters = {
  flagged?: boolean;
  fromDate?: Dayjs;
  toDate?: Dayjs;
  sources?: TicketSource[];
};

type TicketsQueryParams = {
  filters: TicketsQueryFilters;
  isRefreshing?: boolean;
  pagination?: Pagination;
};

type TicketsQueryContextType = {
  applyFilters: (filters: Partial<TicketsQueryFilters>) => void;
  clear: () => void;
  data: Ticket[];
  fetchMore: () => void;
  fetchReason?: FetchReason;
  filters: TicketsQueryFilters;
  hasMore: boolean;
  isLoading: boolean;
  isFetching: boolean;
  refresh: () => void;
};

const TicketsQueryContext = createContext<TicketsQueryContextType>({
  applyFilters: noop,
  clear: noop,
  data: [],
  fetchMore: noop,
  filters: {},
  hasMore: false,
  isLoading: false,
  isFetching: false,
  refresh: noop,
});

function TicketsQueryContextProvider({
  orderId,
  children,
}: React.PropsWithChildren<{ orderId: string }>) {
  const queryKey = makeTicketsQueryContextKey(orderId);
  const queryClient = useQueryClient();

  const [queryParams, setQueryParams] = useState<TicketsQueryParams>({
    filters: {},
  });

  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const query = useInfiniteQuery({
    queryKey: [queryKey, queryParams.filters],
    initialPageParam: queryParams.pagination,
    getNextPageParam: () => queryParams.pagination,
    queryFn: async ({ pageParam, signal }) => {
      const { filters } = queryParams;

      const query: GetV1CompaniesCompanyIdOrdersOrderIdIdTicketsData['query'] = {
        ...derivePaginationQueryParams(pageParam),
        ...deriveFilterQueryParams({ ...filters }),
      };

      const response = await getV1CompaniesCompanyIdOrdersOrderIdIdTickets({
        path: { 'company-id': companyId, 'order-id': orderId },
        query,
        signal,
      });

      const tickets = response.data.data.map((ticket) => Ticket.parse(ticket));
      const pagination = extractPagination(response);

      setQueryParams((state) => ({ ...state, pagination }));

      return tickets;
    },
  });

  const hasMore = Boolean(queryParams.pagination?.after);

  const data = useMemo(() => (query.data?.pages ?? []).flat(), [query.data?.pages]);

  const fetchReason = useMemo(() => {
    if (query.isFetchingNextPage) {
      return FetchReason.INFINITE_SCROLL;
    }

    if (query.isFetching) {
      return FetchReason.INITIAL_FETCH;
    }
  }, [query.isFetchingNextPage, query.isFetching]);

  const fetchMore = useCallback(() => {
    if (hasMore) {
      query.fetchNextPage();
    }
  }, [hasMore, query]);

  const clear = useCallback(() => {
    queryClient.removeQueries({
      predicate(query) {
        return query.queryKey.some((key) => {
          const keyAsString = String(key);

          return (
            keyAsString.startsWith(TICKETS_QUERY_CONTEXT_PREFIX) ||
            keyAsString.startsWith(queryKey)
          );
        });
      },
    });
  }, [queryKey, queryClient]);

  const applyFilters = useCallback<TicketsQueryContextType['applyFilters']>(
    (filters) => {
      clear();
      setQueryParams((state) => ({
        filters: { ...state.filters, ...filters },
      }));
    },
    [clear],
  );

  const refresh = useCallback<TicketsQueryContextType['refresh']>(() => {
    setQueryParams((state) => ({
      filters: { ...state.filters },
      isRefreshing: true,
    }));
  }, []);

  useEffect(() => {
    if (queryParams.isRefreshing) {
      setQueryParams((state) => omit(state, 'isRefreshing'));

      clear();
      query.refetch();
    }
  }, [queryParams.isRefreshing, clear, query]);

  return (
    <TicketsQueryContext.Provider
      value={{
        applyFilters,
        clear,
        data,
        fetchMore,
        fetchReason,
        filters: queryParams.filters,
        hasMore,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        refresh,
      }}
    >
      {children}
    </TicketsQueryContext.Provider>
  );
}

export default TicketsQueryContext;
export { TicketsQueryContextProvider };
